import React from 'react';
import './Pages/PagesCss/LegalInformation.css';

const About = () => {

  const values = [
    {
      icon: "fa-solid fa-pen-nib",
      title: "Creativity",
      description: "We believe fonts and text styles should inspire creativity and expression, not limit it."
    },
    {
      icon: "fa-solid fa-bolt",
      title: "Performance",
      description: "Every tool on Font-Max is built for speed, accuracy, and smooth performance across all devices."
    },
    {
      icon: "fa-solid fa-palette",
      title: "Design Simplicity",
      description: "Our interface is simple, clean, and focused on what matters most your creative work."
    },
    {
      icon: "fa-solid fa-rocket",
      title: "Innovation",
      description: "We’re always adding new text and font tools that make typography easier, smarter, and more fun."
    }
  ];

  const milestones = [
    { year: "2024", title: "Concept", description: "The idea of Font-Max was born to create an all-in-one typography toolkit for everyone." },
    { year: "2025", title: "Launch", description: "Font-Max officially launched, offering tools like Font Generator and Text Converters." },
    { year: "2025", title: "Growth", description: "Expanded with new categories such as Analyze, Clean, and Encode, reaching a global audience." },
    { year: "2025", title: "Next Step", description: "Began developing advanced AI-powered features for smarter font styling and analysis." },
  ];

  return (
    <div className="privacy-policy">
      {/* Header Section */}
      <section className="policy-header">
        <div className="container">
          <div className="header-content">
            <h1>About Font-Max</h1>
            <p className="header-description">
              Font-Max is your complete toolkit for text styling, formatting, and analysis. 
              Whether you’re a designer, developer, or content creator, our tools help you turn words into art.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="policy-content">
        <div className="container">
          <div className="policy-grid">
            
            {/* Quick Navigation */}
            <aside className="table-of-contents">
              <h3>Quick Navigation</h3>
              <nav>
                <ul>
                  <li><a href="#our-story">Our Story</a></li>
                  <li><a href="#our-mission">Our Mission</a></li>
                  <li><a href="#our-values">Our Values</a></li>
                  <li><a href="#our-journey">Our Journey</a></li>
                  <li><a href="#approach">Our Approach</a></li>
                  <li><a href="#contact">Get In Touch</a></li>
                </ul>
              </nav>
            </aside>

            {/* Main Content */}
            <div className="policy-sections">
              
              {/* Story */}
              <section id="our-story" className="policy-section">
                <h2>Our Story</h2>
                <div className="section-content">
                  <h3>From Idea to Platform</h3>
                  <p>
                    <strong>Font-Max started with a simple goal:</strong> to make text styling, formatting, and analysis easy for everyone. 
                    Many existing tools were cluttered, slow, or scattered across different platforms. We decided to build one clean, unified space where creativity meets precision.
                  </p>

                  <p>
                    Founded by <strong>D-Tech Studios</strong>, Font-Max quickly grew into a trusted destination for anyone working with text designers, developers, marketers, and students alike.
                  </p>

                  <p>
                    What began as a small idea turned into a growing ecosystem of smart tools designed to save time, enhance design, and make typography fun again.
                  </p>
                </div>
              </section>

              {/* Mission & Vision */}
              <section id="our-mission" className="policy-section">
                <h2>Our Mission & Vision</h2>
                <div className="section-content">
                  <h3>Our Mission</h3>
                  <p>
                    To empower creators with practical, fast, and reliable tools for text styling, conversion, and analysis—without ads, clutter, or confusion.
                  </p>

                  <h3>Our Vision</h3>
                  <p>
                    To become the most trusted typography platform on the web, bringing together creativity, precision, and simplicity in one place.
                  </p>

                  <div className="rights-grid">
                    <div className="right-item">
                      <h4>Precision</h4>
                      <p>Each tool is tested for consistent and accurate results every time.</p>
                    </div>
                    <div className="right-item">
                      <h4>Accessibility</h4>
                      <p>Designed for everyone—no software installation or sign-ups needed.</p>
                    </div>
                    <div className="right-item">
                      <h4>Speed</h4>
                      <p>Lightweight tools optimized for instant results.</p>
                    </div>
                    <div className="right-item">
                      <h4>Creativity</h4>
                      <p>Turn plain text into visually striking fonts, symbols, and emoji combinations.</p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Values */}
              <section id="our-values" className="policy-section">
                <h2>Our Values</h2>
                <div className="section-content">
                  <p>
                    These core values guide everything we design, build, and deliver at Font-Max.
                  </p>

                  <div className="values-grid">
                    {values.map((value, index) => (
                      <div key={index} className="value-item">
                        <div className="value-icon"><i className={value.icon}></i></div>
                        <div className="value-content">
                          <h4>{value.title}</h4>
                          <p>{value.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="security-features">
                    <h3>Always User-First</h3>
                    <p>
                      <strong>Your experience is our top priority.</strong> We don’t collect unnecessary data or push hidden subscriptions. 
                      Our focus is on creating fast, accessible, and dependable tools for everyone.
                    </p>
                  </div>
                </div>
              </section>

              {/* Journey */}
              <section id="our-journey" className="policy-section">
                <h2>Our Journey</h2>
                <div className="section-content">
                  <p>
                    Every milestone represents our effort to make font and text manipulation tools better and more accessible for all.
                  </p>

                  <div className="timeline">
                    {milestones.map((milestone, index) => (
                      <div key={index} className="timeline-item">
                        <div className="timeline-year">{milestone.year}</div>
                        <div className="timeline-content">
                          <h4>{milestone.title}</h4>
                          <p>{milestone.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="security-features">
                    <h3>Looking Ahead</h3>
                    <p>
                      <strong>Font-Max is just getting started.</strong> Expect more advanced tools, AI-driven features, and design utilities all created to make your workflow faster and smarter.
                    </p>
                  </div>
                </div>
              </section>

              {/* Approach */}
              <section id="approach" className="policy-section">
                <h2>Our Approach</h2>
                <div className="section-content">
                  <h3>Smart Tools, Simple Experience</h3>
                  <p>
                    We focus on a clean, lightweight design with no distractions. Every Font-Max tool is built for one purpose: to work perfectly, every time.
                  </p>

                  <div className="rights-grid">
                    <div className="right-item">
                      <h4>Cross-Platform</h4>
                      <p>All tools work seamlessly on desktop, tablet, and mobile.</p>
                    </div>
                    <div className="right-item">
                      <h4>Ad-Light Interface</h4>
                      <p>Minimal ads and clear layouts to keep your focus on your work.</p>
                    </div>
                    <div className="right-item">
                      <h4>Privacy-Focused</h4>
                      <p>No personal data collection or user tracking.</p>
                    </div>
                    <div className="right-item">
                      <h4>Continuous Improvement</h4>
                      <p>We evolve our tools based on real user feedback and community input.</p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Contact */}
              <section id="contact" className="policy-section">
                <h2>Get In Touch</h2>
                <div className="section-content">
                  <p>
                    Have a suggestion, found a bug, or want to collaborate? 
                    We’d love to hear from you. Your ideas help us make Font-Max better with every update.
                  </p>

                  <div className="contact-info">
                    <div className="contact-item">
                      <strong>Email:</strong> support@font-max.com
                    </div>
                    <div className="contact-item">
                      <strong>Address:</strong> 2568 Madison Ave, League City, TX 77573
                    </div>
                  </div>

                  <div className="security-features">
                    <h3>Join Our Community</h3>
                    <p>
                      <strong>Help shape the future of typography tools.</strong> 
                      Every feature we build starts with user feedback so don’t hesitate to share yours.
                    </p>
                  </div>
                </div>
              </section>

            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="policy-actions">
        <div className="container">
          <div className="actions-content">
            <h3>Explore Our Tools</h3>
            <p>Try our collection of text styling, formatting, and analysis tools today.</p>
            <div className="action-buttons">
              <a href="/URLEncoderDecoder" className="btn btn-primary">Browse Tools</a>
              <a href="/Contact" className="btn btn-secondary">Share Feedback</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
